import { Injectable } from '@angular/core'
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router'
import {
    QUERY_PARAM_KEY_LAYERS,
    QUERY_PARAM_KEY_POSITION_X,
    QUERY_PARAM_KEY_POSITION_Y,
    QUERY_PARAM_KEY_POSITION_Z,
    QUERY_PARAM_KEY_FREE_SELECTIONS,
    QUERY_PARAM_KEY_ZOOM,
    QUERY_PARAM_KEY_HIERARCHICAL_SELECTIONS,
    QUERY_PARAM_KEY_UNSUPERVISED_SELECTIONS,
} from '../constants/query-params-keys'
import { LayerConfig, ViewerPosition } from '../../state/viewer/viewer.state'
import {
    FreeSelection,
    FreeSelectionsForCase,
    HierarchicalSelectionNode,
    HierarchicalSelectionRoot,
    HierarchicalSelectionsForCase,
    UnsupervisedSelectionNode,
    UnsupervisedSelectionRoot,
    UnsupervisedSelectionsForCase,
} from '../../state/selection/selection.state'
import {
    FreeSelectionQueryParam,
    FreeSelectionQueryParams,
    HierarchicalSelectionNodeQueryParam,
    HierarchicalSelectionQueryParams,
    HierarchicalSelectionRootQueryParam,
    ViewerQueryParams,
} from '../model/query-params-types'
import { closeCaseManagementDialog } from '../utils/case-management-dialog-utils'
import { CaseId } from '../../state/case/case.state'
import { map, Observable } from 'rxjs'

@Injectable({
    providedIn: 'root',
})
export class RouterUtilsService {
    constructor(private readonly router: Router, private readonly route: ActivatedRoute) {}

    goToCase(caseId: CaseId): void {
        this.router.navigate(['/cases', caseId]).then(() => closeCaseManagementDialog())
    }

    goToCaseReload(caseId: CaseId): void {
        this.router.navigate(['/cases', caseId]).then(() => window.location.reload())
    }

    updatePositionQueryParam(position: [number, number, number]): void {
        this.updateQueryParams({
            [QUERY_PARAM_KEY_POSITION_X]: position[0],
            [QUERY_PARAM_KEY_POSITION_Y]: position[1],
            [QUERY_PARAM_KEY_POSITION_Z]: position[2],
        })
    }

    updateZoomQueryParam(zoom: number): void {
        this.updateQueryParams({ [QUERY_PARAM_KEY_ZOOM]: zoom })
    }

    updateLayerQueryParam(layers: LayerConfig[]): void {
        this.updateQueryParams({
            [QUERY_PARAM_KEY_LAYERS]: JSON.stringify(
                layers.map(
                    (l: LayerConfig): LayerConfig => ({
                        id: l.id,
                        visible: l.visible,
                        shaderColor: l.shaderColor,
                        opacity: l.opacity,
                        opacityUnselected: l.opacityUnselected,
                        saturation: l.saturation,
                        brightness: l.brightness,
                        contrast: l.contrast,
                        segmentationColor: l.segmentationColor,
                        normalize: l.normalize,
                    })
                )
            ),
        })
    }

    updateSelectionQueryParam(
        freeSelections: FreeSelectionsForCase,
        hierarchicalSelections: HierarchicalSelectionsForCase,
        unsupervisedSelections: UnsupervisedSelectionsForCase
    ): void {
        this.updateQueryParams({
            [QUERY_PARAM_KEY_FREE_SELECTIONS]: JSON.stringify(
                Array.from(freeSelections.entries()).map(
                    ([key, selection]: [string, FreeSelection]): [
                        string,
                        FreeSelectionQueryParam
                    ] => [
                        key,
                        {
                            name: selection.name,
                            id: selection.id,
                            type: selection.type,
                            annotationVisible: selection.annotationVisible,
                            annotationOpacity: selection.annotationOpacity,
                            annotationColor: selection.annotationColor,
                        },
                    ]
                )
            ),
            [QUERY_PARAM_KEY_HIERARCHICAL_SELECTIONS]: JSON.stringify(
                Array.from(hierarchicalSelections.entries()).map(
                    ([key, selection]: [string, HierarchicalSelectionRoot]): [
                        string,
                        HierarchicalSelectionRootQueryParam
                    ] => [
                        key,
                        this.parseHierarchicalSelectionRootToHierarchicalSelectionRootQueryParam(
                            selection
                        ),
                    ]
                )
            ),
            [QUERY_PARAM_KEY_UNSUPERVISED_SELECTIONS]: JSON.stringify(
                Array.from(unsupervisedSelections.entries()).map(
                    ([key, selection]: [string, UnsupervisedSelectionRoot]): [
                        string,
                        HierarchicalSelectionRootQueryParam
                    ] => [
                        key,
                        this.parseHierarchicalSelectionRootToHierarchicalSelectionRootQueryParam(
                            selection
                        ),
                    ]
                )
            ),
        })
        return
    }

    private updateQueryParams(params: any): void {
        this.router.navigate([], {
            queryParams: params,
            queryParamsHandling: 'merge',
            relativeTo: this.route,
            replaceUrl: true,
        })
    }

    getViewerQueryParams(route: ActivatedRouteSnapshot): ViewerQueryParams {
        return {
            position: this.getPositionFromQueryParams(route),
            zoom: this.getZoomFromQueryParams(route),
            layers: this.getLayersFromQueryParams(route),
        }
    }

    private getPositionFromQueryParams(route: ActivatedRouteSnapshot): ViewerPosition | undefined {
        const posX = Number(route.queryParamMap.get(QUERY_PARAM_KEY_POSITION_X) ?? undefined)
        const posY = Number(route.queryParamMap.get(QUERY_PARAM_KEY_POSITION_Y) ?? undefined)
        const posZ = Number(route.queryParamMap.get(QUERY_PARAM_KEY_POSITION_Z) ?? undefined)
        if (isNaN(posX) || isNaN(posY) || isNaN(posZ)) {
            return undefined
        }

        // TODO: how can we handle the 0.5 additions
        return [posX, posY, posZ]
    }

    private getZoomFromQueryParams(route: ActivatedRouteSnapshot): number | undefined {
        const zoom = Number(route.queryParamMap.get(QUERY_PARAM_KEY_ZOOM) ?? undefined)
        if (isNaN(zoom)) {
            return undefined
        }
        if (zoom === 0) {
            console.warn('getZoomFromQueryParams(): attempt to set zoom to 0, using 1 instead')
            return 1
        }
        return zoom
    }

    private getLayersFromQueryParams(route: ActivatedRouteSnapshot): LayerConfig[] | undefined {
        return JSON.parse(route.queryParamMap.get(QUERY_PARAM_KEY_LAYERS) ?? 'null') ?? []
    }

    getFreeSelectionQueryParams(): Observable<FreeSelectionQueryParams> {
        return this.route.queryParamMap.pipe(
            map(value => {
                const selections: FreeSelectionQueryParams = new Map(
                    (JSON.parse(value.get(QUERY_PARAM_KEY_FREE_SELECTIONS) ?? 'null') ?? []).map(
                        ([key, selection]: [string, FreeSelectionQueryParam]): [
                            string,
                            FreeSelectionQueryParam
                        ] => [key, selection]
                    )
                )
                return selections
            })
        )
    }

    getHierarchicalSelectionQueryParams(): Observable<HierarchicalSelectionQueryParams> {
        return this.route.queryParamMap.pipe(
            map(value => {
                const selections: HierarchicalSelectionQueryParams = new Map(
                    (
                        JSON.parse(value.get(QUERY_PARAM_KEY_HIERARCHICAL_SELECTIONS) ?? 'null') ??
                        []
                    ).map(
                        ([key, selection]: [string, HierarchicalSelectionRootQueryParam]): [
                            string,
                            HierarchicalSelectionRootQueryParam
                        ] => [key, selection]
                    )
                )
                return selections
            })
        )
    }
    getUnsupervisedSelectionQueryParams(): Observable<HierarchicalSelectionQueryParams> {
        return this.route.queryParamMap.pipe(
            map(value => {
                const selections: HierarchicalSelectionQueryParams = new Map(
                    (
                        JSON.parse(value.get(QUERY_PARAM_KEY_UNSUPERVISED_SELECTIONS) ?? 'null') ??
                        []
                    ).map(
                        ([key, selection]: [string, HierarchicalSelectionRootQueryParam]): [
                            string,
                            HierarchicalSelectionRootQueryParam
                        ] => [key, selection]
                    )
                )
                return selections
            })
        )
    }

    parseHierarchicalSelectionRootToHierarchicalSelectionRootQueryParam(
        selection: HierarchicalSelectionRoot | UnsupervisedSelectionRoot
    ): HierarchicalSelectionRootQueryParam {
        return {
            name: selection.name,
            children: selection.children?.map(child =>
                this.parseHierarchicalSelectionNodeToHierarchicalSelectionNodeQueryParam(child)
            ),
        }
    }

    parseHierarchicalSelectionNodeToHierarchicalSelectionNodeQueryParam(
        selection: HierarchicalSelectionNode | UnsupervisedSelectionNode
    ): HierarchicalSelectionNodeQueryParam {
        return {
            name: selection.name,
            id: selection.id,
            annotationVisible: selection.annotationVisible,
            annotationColor: selection.annotationColor,
            annotationOpacity: selection.annotationOpacity,
            children: selection.children?.map(child =>
                this.parseHierarchicalSelectionNodeToHierarchicalSelectionNodeQueryParam(child)
            ),
        }
    }
}
